import {
  type DatasourceDataRequest,
  type DatasourceDetailsRequest,
} from '@atlaskit/linking-types';

import { DatasourceClient } from '@canvas/atlassian-client/datasource';
import { retryWithBackoff } from '@canvas/retry';

import { SiteService } from '../site-service';

import { getDatasourceConfig, type DatasourceType } from './datasource-config';
export { type DatasourceType } from './datasource-config';

const fetcher = async (input: RequestInfo, init?: RequestInit) =>
  await retryWithBackoff({
    callback: () => globalThis.fetch(input, init),
    maxRetries: 3,
  });

export class DatasourceService {
  public constructor(
    private datasourceType: DatasourceType,
    private client = new DatasourceClient(
      fetcher,
      new URL('/gateway/api/object-resolver', globalThis.location.origin),
    ),
  ) {}

  private getDatasourceConfig() {
    return getDatasourceConfig(this.datasourceType);
  }

  public getData(params: DatasourceDataRequest, headers?: HeadersInit) {
    const { datasourceId } = this.getDatasourceConfig();
    return this.client.getData(datasourceId, params, headers);
  }

  public async getAvailableSites() {
    const { productName } = this.getDatasourceConfig();
    const siteService = SiteService.getInstance();
    const availableSites = siteService.getSites(productName);
    if (availableSites.length === 0) {
      await siteService.loadSites();
      return siteService.getSites(productName);
    }
    return availableSites;
  }

  public async getSupportedProperties(params: DatasourceDetailsRequest) {
    const { datasourceId } = this.getDatasourceConfig();
    const response = await this.client.getDetails(datasourceId, params);
    return response?.data?.schema?.properties ?? [];
  }

  public getId() {
    const { datasourceId } = this.getDatasourceConfig();
    return datasourceId;
  }
}

export type InvokeAction = {
  actionType: string;
  resourceIdentifiers: Record<string, unknown>;
  payload?: unknown;
};

export type InvokeRequest = {
  providerKey: string;
  action: InvokeAction;
};

export async function invokeObjectResolver<T = unknown>(req: InvokeRequest): Promise<T> {
  const url = new URL('/gateway/api/object-resolver/invoke', globalThis.location.origin);

  const res = await fetcher(url.toString(), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
    credentials: 'include',
  });

  const text = await res.text();
  if (!res.ok) {
    const { providerKey, action } = req;
    throw new Error(
      `Invoke failed (${res.status}) providerKey=${providerKey} actionType=${
        action?.actionType
      } body=${text?.slice(0, 500)}`,
    );
  }

  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}

export type ExecuteActionRequest = {
  integrationKey: string;
  actionKey: string;
  parameters: unknown;
};

export async function executeObjectResolverAction<T = unknown>(
  req: ExecuteActionRequest,
): Promise<T> {
  const url = new URL(
    '/gateway/api/object-resolver/actions/execute',
    globalThis.location.origin,
  );

  const res = await fetcher(url.toString(), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(req),
  });

  const text = await res.text();
  if (!res.ok) {
    const { integrationKey, actionKey } = req;
    throw new Error(
      `Execute failed (${res.status}) integrationKey=${integrationKey} actionKey=${actionKey}}`,
    );
  }

  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}
